import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { generateRestoSlug } from "../utils/RestoSlugUtils";
import LoadingIndicator from "./LoadingIndicator";
import { CloseIcon } from "./Icon";
import { sidebarStateManager } from "../utils/SidebarStateManager";
import { useSidebarState } from "../hooks/useSidebarState";
import AnalyticsManager from "../utils/AnalyticsManager";
import RestoApiService, { BacklogEntry, BacklogResponse, BacklogSummary } from "../utils/RestoApiService";
import styles from "./RestaurantDetailsSidebar.module.css";

interface BacklogContentProps {
  initialData?: { entries?: BacklogResponse; summary?: BacklogSummary };
}

const getScoreEmoji = (score: number) => {
  if (score === 2) return "🤮";
  if (score === 1) return "💀";
  return "❓";
};

const restoApiService = new RestoApiService();
const PAGE_SIZE = 100;

const BacklogContent: React.FC<BacklogContentProps> = ({ initialData }) => {
  const { t, i18n } = useTranslation();
  const { closeSidebar } = useSidebarState(sidebarStateManager);
  const [entries, setEntries] = useState<BacklogEntry[]>(initialData?.entries?.entries || []);
  const [totalCount, setTotalCount] = useState(initialData?.entries?.totalCount ?? 0);
  const [summary, setSummary] = useState<BacklogSummary | null>(initialData?.summary || null);
  const [loading, setLoading] = useState(!initialData?.entries);
  const [loadingMore, setLoadingMore] = useState(false);
  const [province, setProvince] = useState<string | null>(null);
  const lang = i18n.language || 'en';

  const fetchPage = async (prov: string | null, offset: number, append: boolean) => {
    const result = await restoApiService.fetchBacklog({ province: prov || undefined, limit: PAGE_SIZE, offset });
    if (result.success && result.data) {
      setEntries((prev) => (append ? [...prev, ...result.data!.entries] : result.data!.entries));
      setTotalCount(result.data.totalCount);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (!initialData?.summary) {
        const s = await restoApiService.fetchBacklogSummary();
        if (s.success && s.data) setSummary(s.data);
      }
      if (!initialData?.entries) {
        setLoading(true);
        await fetchPage(null, 0, false);
        setLoading(false);
      }
      AnalyticsManager.getInstance().trackEvent('backlog_page_view', {
        total: initialData?.summary?.totalCount,
        event_category: 'content',
      });
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectProvince = async (slug: string | null) => {
    setProvince(slug);
    setLoading(true);
    AnalyticsManager.getInstance().trackEvent('backlog_filter', {
      province: slug || 'all',
      event_category: 'engagement',
    });
    await fetchPage(slug, 0, false);
    setLoading(false);
  };

  const loadMore = async () => {
    setLoadingMore(true);
    AnalyticsManager.getInstance().trackEvent('backlog_load_more', {
      offset: entries.length,
      event_category: 'engagement',
    });
    await fetchPage(province, entries.length, true);
    setLoadingMore(false);
  };

  const handleRestaurantClick = (e: React.MouseEvent, entry: BacklogEntry) => {
    e.preventDefault();
    AnalyticsManager.getInstance().trackEvent('backlog_restaurant_click', {
      restaurant_id: entry.id,
      age_years: entry.ageYears,
      event_category: 'navigation',
    });
    window.dispatchEvent(new CustomEvent('restaurantSelected', {
      detail: { restaurantId: entry.id.toString(), openSidebar: true }
    }));
    window.history.pushState({}, '', `/${lang}/restaurant/${generateRestoSlug(entry.name, entry.id)}`);
  };

  const formatDate = (epoch: number) =>
    new Date(epoch).toLocaleDateString(lang === 'en' ? 'en-GB' : `${lang}-BE`, { year: 'numeric', month: 'long' });

  const provinceName = (f: { nameNl: string; nameFr: string; name: string }) =>
    lang === 'nl' ? f.nameNl : lang === 'fr' ? f.nameFr : f.name;

  const shortAddress = (addr: string) => addr.split(/\d{4}/)[0].trim();
  const municipality = (addr: string) => {
    const m = addr.match(/\d{4}\s+(.+?)\s+Belgi/);
    return m ? m[1] : '';
  };

  return (
    <div className="flex flex-col h-full">
      <div className={`flex-grow overflow-y-auto overflow-x-hidden ${styles.customScrollbar}`}>
        <div className="pl-4 pr-2 pt-4 pb-16">
          {/* Header */}
          <div className="flex justify-between items-center mb-1">
            <div className="min-w-0">
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">
                {t('backlog.title', 'Inspection backlog')}
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('backlog.subtitle', 'Active establishments whose last FAVV inspection was unfavorable and at least 2 years ago')}
              </p>
            </div>
            <button
              onClick={closeSidebar}
              className="p-2 text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 rounded-lg transition-all duration-200 flex-shrink-0"
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>

          {/* Headline stats */}
          {summary && (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 mb-3 mt-3">
              <div className="text-3xl font-bold text-gray-800 dark:text-gray-100">
                {summary.totalCount.toLocaleString(lang)}
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {t('backlog.statExplain', 'establishments with an unfavorable score and no inspection result in 2+ years')}
              </p>
              {summary.oldestInspection && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  {t('backlog.oldest', 'Oldest unfavorable result: {{date}}', { date: formatDate(summary.oldestInspection) })}
                </p>
              )}
            </div>
          )}

          {/* Province filter */}
          {summary && summary.byProvince.length > 0 && (
            <div className="flex gap-1.5 mb-3 flex-wrap">
              <button
                onClick={() => selectProvince(null)}
                className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                  province === null
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
              >
                {t('backlog.allProvinces', 'All')} ({summary.totalCount})
              </button>
              {summary.byProvince.map((f) => (
                <button
                  key={f.slug}
                  onClick={() => selectProvince(f.slug)}
                  className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                    province === f.slug
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {provinceName(f)} ({f.count})
                </button>
              ))}
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-12">
              <LoadingIndicator isLoading={true} />
            </div>
          )}

          {!loading && entries.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                {t('backlog.noData', 'No data available')}
              </p>
            </div>
          )}

          {!loading && entries.length > 0 && (
            <div className="space-y-1.5" data-testid="backlog-list">
              {entries.map((entry) => (
                <a
                  key={entry.id}
                  href={`/${lang}/restaurant/${generateRestoSlug(entry.name, entry.id)}`}
                  onClick={(e) => handleRestaurantClick(e, entry)}
                  className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/60 transition-colors flex justify-between items-center gap-2 no-underline"
                >
                  <div className="min-w-0 flex-1">
                    <h5 className="text-sm font-medium text-gray-800 dark:text-gray-100 truncate">{entry.name}</h5>
                    <p className="text-xs text-gray-400 truncate">
                      {entry.postalCode ? `${entry.postalCode} ${municipality(entry.address)}` : shortAddress(entry.address)}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {t('backlog.lastInspection', 'Last inspection {{date}} · {{years}} years ago', {
                        date: formatDate(entry.inspectionFrom),
                        years: entry.ageYears,
                      })}
                    </p>
                  </div>
                  <span className="text-2xl flex-shrink-0" title={`${entry.score}/5`}>
                    {getScoreEmoji(entry.score)}
                  </span>
                </a>
              ))}
              {entries.length < totalCount && (
                <button
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="w-full py-2.5 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 rounded-lg transition-colors disabled:opacity-50"
                >
                  {loadingMore ? '…' : t('backlog.loadMore', 'Show more ({{shown}}/{{total}})', { shown: entries.length, total: totalCount })}
                </button>
              )}
            </div>
          )}

          {/* Methodology / factual framing */}
          <p className="text-xs text-gray-400 dark:text-gray-500 leading-relaxed mt-4">
            {t('backlog.methodology', 'Based on public FAVV inspection results. An old score says nothing about the current situation; there is simply no more recent result available in the public data.')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default BacklogContent;
